import React, { useState } from 'react';
import { useGetCourses, useIncrementWatchedLectures } from '../api/coursesApi';
import { Course } from '../types';
import Button from '@/components/ui/Button';
import EditCourseModal from './EditCourseModal';

const CourseCard: React.FC<{ course: Course }> = ({ course }) => {
  const incrementMutation = useIncrementWatchedLectures();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const isCompleted = course.watchedLectures >= course.lectureCount;
  const progressPercentage = Math.round((course.watchedLectures / course.lectureCount) * 100);

  const handleIncrement = () => {
    if (!isCompleted) {
      incrementMutation.mutate(course.id);
    }
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
  };

  return (
    <>
      <div className={`bg-white text-text rounded-lg shadow-md p-6 border-2 transition-colors ${
        isCompleted ? 'border-green-500 bg-green-50' : 'border-gray-200'
      }`}>
        <div className="flex justify-between items-start mb-4 gap-3">
          <h3 className="text-xl font-semibold text-gray-900 flex-1 min-w-0">{course.name}</h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isCompleted && (
              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full font-medium whitespace-nowrap">
                ✓ Completed
              </span>
            )}
            <button
              onClick={handleEditClick}
              className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
              title="Edit course"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{course.watchedLectures}/{course.lectureCount} lectures</span>
          </div>
          <div className="w-full bg-accent/40 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                isCompleted ? 'bg-primary' : 'bg-primary'
              }`}
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <div className="text-right text-xs text-gray-500 mt-1">
            {progressPercentage}% complete
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Added: {new Date(course.createdAt).toLocaleDateString()}
          </div>
          
          <Button
            onClick={handleIncrement}
            disabled={isCompleted || incrementMutation.isPending}
            variant={isCompleted ? 'outline' : 'primary'}
            className={`px-3 py-2 ${isCompleted ? 'cursor-not-allowed' : ''}`}
            title={isCompleted ? 'Course completed' : `Add 1 lecture (${course.lectureCount - course.watchedLectures} left)`}
          >
            {incrementMutation.isPending ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : isCompleted ? (
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Done
              </span>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            )}
          </Button>
        </div>
      </div>

      <EditCourseModal
        course={course}
        isOpen={isEditModalOpen}
        onClose={handleEditClose}
      />
    </>
  );
};

const CoursesList: React.FC = () => {
  const { data: courses, isLoading, error } = useGetCourses();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6 border border-gray-200 animate-pulse">
            <div className="h-6 bg-gray-300 rounded mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-2 bg-gray-300 rounded"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md mx-auto">
          <p className="text-red-600">Failed to load courses. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!courses || courses.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No courses yet</h3>
          <p className="mt-2 text-gray-500">
            Click the "Add New Course" button above to get started.
          </p>
        </div>
      </div>
    );
  }

  // Sort courses to show incomplete first, then completed
  const sortedCourses = [...courses].sort((a, b) => {
    const aCompleted = a.watchedLectures >= a.lectureCount;
    const bCompleted = b.watchedLectures >= b.lectureCount;
    
    if (aCompleted === bCompleted) {
      // If both have same completion status, sort by last accessed (most recent first)
      return new Date(b.lastAccessed).getTime() - new Date(a.lastAccessed).getTime();
    }
    
    // Show incomplete courses first
    return aCompleted ? 1 : -1;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          Your Courses ({courses.length})
        </h2>
        {courses.length > 0 && (
          <div className="text-sm text-gray-600">
            {courses.filter(c => c.watchedLectures >= c.lectureCount).length} completed
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedCourses.map((course) => (
          <CourseCard key={course.id} course={course} />
        ))}
      </div>
    </div>
  );
};

export default CoursesList; 